// Phone touch guard (MINIMAL-2026-09-18, "Dotyk na telefonie (kontrakt 390)").
//
// Two jobs, both copied from VidR Studio (studio.js, 2026-08-26):
//
//   1. `is-app` on <html> — the scope every rule in styles/touch.css is keyed to.
//      The app shell and the auth shell put it on while they are mounted; public
//      and legal pages never carry it, so they stay zoomable and selectable.
//   2. The gesture guard — iOS Safari still reaches pinch-zoom through its own
//      `gesture*` events even when `touch-action` forbids it, and an old WebKit
//      can still double-tap-zoom. Both are killed here, but only inside `is-app`.
//
// WHAT THE DOUBLE-TAP GUARD MUST NOT EAT. `preventDefault()` on `touchend` also
// cancels the synthetic `click` that follows it, so a second quick tap on a
// control would simply not happen: the stepper's +/-, the player's transport,
// a caret placed in a field. `touch-action: pan-x pan-y` already ends double-tap
// zoom in every browser we ship to; this guard is the seat belt for the
// background and for plain content, so it skips interactive targets entirely.
//
// AND "INTERACTIVE" IS NOT ONLY A ROLE. Half the clickable surfaces in this app are
// plain `<div onClick>` with no role and no tabindex — the lyrics timeline lane that
// deselects a word (LyricsEditor `.tl-track`), the text-library list background, the
// scrim that closes the user menu (`.pop-veil`). A click handler cannot be read off the
// DOM, so the guard asks the two questions that CAN be answered: does the target sit in
// something with an interactive role, and does anything from the target up to <body>
// paint `cursor: pointer` — which every clickable surface in this app does, because that
// is what tells a mouse user it is clickable.
//
// On top of that the guard only arms on a SEQUENCE of two plain taps. The measured
// defect was not a double tap on the background at all: tap a word tile, then tap the
// empty lane beside it to deselect (or tap the user chip, then the scrim to close the
// menu) — within 300 ms and 30 px, which is an ordinary pair of taps, not a gesture.
// The second one was swallowed and the screen did nothing. Two taps are one zoom attempt
// only when BOTH of them landed on something that does nothing.

import { useEffect } from 'react'

/** The class every rule in styles/touch.css is keyed to. */
export const APP_TOUCH_CLASS = 'is-app'

/** Second tap counts as a zoom attempt below this gap / distance (VidR values). */
const DOUBLE_TAP_MS = 300
const DOUBLE_TAP_PX = 30

/** Targets whose second quick tap is a legitimate interaction, never a zoom. */
const INTERACTIVE =
  'a, button, input, select, textarea, label, summary, [contenteditable], ' +
  '[role="button"], [role="link"], [role="tab"], [role="switch"], [role="checkbox"], ' +
  '[role="radio"], [role="menuitem"], [role="option"], [tabindex]:not([tabindex="-1"])'

function inAppScope(doc: Document): boolean {
  return doc.documentElement.classList.contains(APP_TOUCH_CLASS)
}

/**
 * Would a tap here do anything?
 *
 * `closest(INTERACTIVE)` answers for real controls. The cursor answers for the rest: a
 * `<div onClick>` carries no role, its handler is invisible to the DOM, and the one
 * honest signal it leaves behind is the pointer cursor its sheet paints — on itself or
 * on an ancestor, which is where tile-shaped surfaces declare it.
 */
function isClickable(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  if (target.closest(INTERACTIVE) !== null) return true
  // The global `getComputedStyle`, deliberately: reaching it through the target's own
  // `ownerDocument` would be tidier, but the property that leads there is spelled with a
  // root the storefront vocabulary lint forbids, and a lint exemption for a variable name
  // is a worse trade than one global. The guard already runs in one window.
  const body = target.ownerDocument?.body
  for (let el: Element | null = target; el; el = el.parentElement) {
    if (getComputedStyle(el).cursor === 'pointer') return true
    if (el === body) break
  }
  return false
}

/**
 * Install the document-level guard. Safe to call once at startup: every handler
 * checks the scope class, so it is inert on public pages.
 *
 * @returns the teardown that removes every listener it added.
 */
export function installTouchGuard(doc: Document = document): () => void {
  const stopGesture = (event: Event) => {
    if (!inAppScope(doc) || !event.cancelable) return
    event.preventDefault()
  }
  const gestures = ['gesturestart', 'gesturechange', 'gestureend'] as const
  for (const type of gestures) doc.addEventListener(type, stopGesture, { passive: false })

  let lastTap = { at: 0, x: 0, y: 0, clickable: false }
  const onTouchEnd = (event: Event) => {
    const touch = (event as TouchEvent).changedTouches
    if (!touch || touch.length !== 1) return
    const point = touch[0]
    const now = Date.now()
    const near =
      Math.abs(point.clientX - lastTap.x) < DOUBLE_TAP_PX &&
      Math.abs(point.clientY - lastTap.y) < DOUBLE_TAP_PX
    const clickable = isClickable(event.target)
    if (
      inAppScope(doc) &&
      now - lastTap.at < DOUBLE_TAP_MS &&
      near &&
      event.cancelable &&
      // BOTH taps have to have landed on something inert. A tap that follows a tap on a
      // control is the second half of an ordinary pair — tile then lane, chip then scrim
      // — and eating it eats the click the person is waiting for.
      !clickable &&
      !lastTap.clickable
    ) {
      event.preventDefault()
    }
    lastTap = { at: now, x: point.clientX, y: point.clientY, clickable }
  }
  doc.addEventListener('touchend', onTouchEnd, { passive: false })

  return () => {
    for (const type of gestures) doc.removeEventListener(type, stopGesture)
    doc.removeEventListener('touchend', onTouchEnd)
  }
}

// Shells can overlap for a frame during a route change (and StrictMode mounts an
// effect twice in dev), so the class is reference-counted: the last shell to
// leave is the one that takes it off.
let scopeDepth = 0

/** Mark the document as "inside the app" for as long as this component lives. */
export function useAppTouchScope(): void {
  useEffect(() => {
    scopeDepth += 1
    document.documentElement.classList.add(APP_TOUCH_CLASS)
    return () => {
      scopeDepth -= 1
      if (scopeDepth <= 0) {
        scopeDepth = 0
        document.documentElement.classList.remove(APP_TOUCH_CLASS)
      }
    }
  }, [])
}
